import type { CuttingDocument, EdgeType } from '../model/types';

/**
 * Block 14 «Раскрой» — экспорт списка деталей в наш стандартный 16-колоночный CSV
 * (TRD §7.6). Чистая функция без побочных эффектов: BOM и скачивание — на стороне
 * вызова (`ResultActions.handleExportParts`).
 *
 * Формат под русский Excel: разделитель полей «;», CRLF между строками; UTF-8 BOM
 * добавляет вызывающий код. Заголовок вербатим §7.6. Тип материала выводится из
 * текста материала (ЛДСП/МДФ/ХДФ), «Текстура» — из режима вращения (forbid → 1).
 * Квотирование — условное (RFC 4180): только при «;», кавычке или CR/LF в ячейке.
 */

/**
 * Заголовок — вербатим §7.6, 16 колонок. Общий источник колонок для CSV и XLSX
 * (ADR-0096): и `buildPartsCsv`, и `buildPartsXlsxBlob` строят лист из этого же
 * массива, чтобы форматы не разъехались. Порядок и тексты менять нельзя (golden).
 */
export const PARTS_HEADER: readonly string[] = [
  'Позиция',
  'Наименование',
  'Длина',
  'Ширина',
  'Количество',
  'Вращать',
  'Материал',
  'Тип материала',
  'Толщина',
  'Кромка Д1',
  'Кромка Д2',
  'Кромка Ш1',
  'Кромка Ш2',
  'Пазы',
  'Обработка',
  'Текстура',
];

/** Условное квотирование: оборачиваем только если внутри есть «;», кавычка или CR/LF. */
function escapeField(value: string): string {
  return /[;"\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Метка типа паза V/U/L для одной стороны (пустой паз сюда не попадает). */
const GROOVE_LABEL: Record<'v' | 'u' | 'l', string> = { v: 'V', u: 'U', l: 'L' };

/**
 * Построить строки списка деталей — общий 16-колоночный маппинг (§7.6, ADR-0096).
 * Возвращает per-part массив из 16 строковых ячеек БЕЗ escape и БЕЗ join: CSV
 * добавляет квотирование и «;», XLSX отдаёт ячейки SheetJS как есть. Маппинг
 * колонок един для обоих форматов — единственный источник истины колонок.
 */
export function buildPartsRows(doc: CuttingDocument): string[][] {
  /**
   * Толщина кромки стороны (формат toFixed(1)). В проекте определены только
   * edge1/edge2 (type1/type2) — type3/type4/'' дают undefined → '0.0' (норма).
   */
  const edgeThk = (side: EdgeType): string => {
    const thickness =
      side === 'type1'
        ? doc.edges.edge1.thickness
        : side === 'type2'
          ? doc.edges.edge2.thickness
          : undefined;
    return (thickness ?? 0).toFixed(1);
  };

  /** Тип материала из свободного текста: ЛДСП → МДФ → ХДФ → ''. */
  const materialType = (matText: string): string => {
    if (/ЛДСП/i.test(matText)) return 'ЛДСП';
    if (/МДФ/i.test(matText)) return 'МДФ';
    if (/ХДФ/i.test(matText)) return 'ХДФ';
    return '';
  };

  const thicknessCell =
    doc.meta.materialThickness != null ? String(Math.round(doc.meta.materialThickness)) : '';

  return doc.parts.map((part, i) => {
    const matText = part.material?.trim() || doc.meta.materialText || '';

    // Пазы: стороны в порядке [top, right, bottom, left], непустые → `V-top`, join ','.
    const grooves = (['top', 'right', 'bottom', 'left'] as const)
      .filter((side) => part.grooves[side] !== '')
      .map((side) => `${GROOVE_LABEL[part.grooves[side] as 'v' | 'u' | 'l']}-${side}`)
      .join(',');

    return [
      String(i + 1), // Позиция
      part.name, // Наименование
      String(Math.round(part.length)), // Длина
      String(Math.round(part.width)), // Ширина
      String(part.qty), // Количество
      part.rotation === 'forbid' ? 'Нет' : 'Да', // Вращать (allow/force → Да)
      matText, // Материал
      materialType(matText), // Тип материала
      thicknessCell, // Толщина
      edgeThk(part.edges.top), // Кромка Д1 (длинные стороны = top/bottom)
      edgeThk(part.edges.bottom), // Кромка Д2
      edgeThk(part.edges.left), // Кромка Ш1 (ширинные стороны = left/right)
      edgeThk(part.edges.right), // Кромка Ш2
      grooves, // Пазы
      part.processing || '', // Обработка
      part.rotation === 'forbid' ? '1' : '0', // Текстура (forbid → 1)
    ];
  });
}

export function buildPartsCsv(doc: CuttingDocument): string {
  const lines = [PARTS_HEADER.join(';')];
  for (const row of buildPartsRows(doc)) lines.push(row.map(escapeField).join(';'));
  return lines.join('\r\n');
}
